import React, { useState, useEffect, useCallback, useRef } from 'react';

const POLL_MS = 4000;

const fmt = (value, limit = 260) => {
  const s = String(value ?? '').trim().replace(/\s+/g, ' ');
  return limit > 0 && s.length > limit ? s.slice(0, limit - 1) + '…' : s;
};

const fmtTs = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
};

const gapColor = (entry) => {
  if (entry.error) return '#e53935';
  if (entry.gap_total > 0) return '#fb8c00';
  return '#43a047';
};

const sourceLabel = (src) => {
  if (!src) return '—';
  const map = {
    user: 'User',
    tick_loop: 'TickLoop',
    runner_message: 'Chat',
    rivet: 'Rivet',
    poison_recovery: 'PoisonRec',
    subject_veto_recovery: 'VetoRec',
    escalation: 'Escalation',
    ohb: 'OHB',
    l1_shadow: 'L1Shadow',
    anchor_migration: 'Anchor',
    ocm_continuity: 'Continuity',
  };
  for (const [k, v] of Object.entries(map)) {
    if (src.toLowerCase().startsWith(k)) return v;
  }
  return fmt(src, 20);
};

const LANE_COLOR = {
  foreground: '#1976d2',
  support: '#7b1fa2',
  background: '#546e7a',
  deferred: '#795548',
  park: '#9e9e9e',
  drop: '#9e9e9e',
  close: '#9e9e9e',
};

function FlowPill({ flow }) {
  const color = LANE_COLOR[flow.lane] || '#757575';
  return (
    <span style={{
      display: 'inline-block',
      marginRight: 4,
      marginBottom: 3,
      padding: '1px 6px',
      borderRadius: 10,
      fontSize: 11,
      background: color + '22',
      border: `1px solid ${color}44`,
      color: color,
      fontFamily: 'monospace',
    }}>
      {flow.lane || '?'} · {fmt(flow.flow_id, 28)}
      {flow.fate ? ` → ${flow.fate}` : ''}
    </span>
  );
}

function ActionPill({ action }) {
  const ok = !action.status || action.status === 'completed' || action.status === 'ok';
  return (
    <span style={{
      display: 'inline-block',
      marginRight: 4,
      marginBottom: 3,
      padding: '1px 6px',
      borderRadius: 10,
      fontSize: 11,
      background: ok ? '#1b5e2022' : '#b71c1c22',
      border: `1px solid ${ok ? '#2e7d3244' : '#c62828aa'}`,
      color: ok ? '#2e7d32' : '#c62828',
      fontFamily: 'monospace',
    }}>
      {fmt(action.kind, 30)} {action.status ? `[${action.status}]` : ''}
    </span>
  );
}

function DetailPanel({ entry }) {
  if (!entry) {
    return (
      <div style={{ padding: 24, color: 'var(--muted, #888)', fontSize: 14 }}>
        Выберите OCM слева
      </div>
    );
  }

  const gapOk = !entry.error && entry.gap_total === 0;

  return (
    <div style={{ padding: '16px 20px', overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ marginBottom: 16, borderBottom: '1px solid var(--border, #333)', paddingBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 18, fontFamily: 'monospace' }}>
            OCM #{entry.ocm_n}
          </span>
          <span style={{
            padding: '2px 10px', borderRadius: 12, fontSize: 12,
            background: gapOk ? '#43a04722' : '#fb8c0022',
            color: gapOk ? '#43a047' : '#fb8c00',
            border: `1px solid ${gapOk ? '#43a04744' : '#fb8c0044'}`,
          }}>
            {entry.error ? `error: ${entry.error}` : gapOk ? 'clean' : `gap=${entry.gap_total}`}
          </span>
          {entry.elapsed_s != null && (
            <span style={{ fontSize: 12, color: 'var(--muted, #888)' }}>
              {entry.elapsed_s}s
            </span>
          )}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted, #888)', fontFamily: 'monospace' }}>
          {fmtTs(entry.ts)} · {entry.mode || '—'} · {entry.model ? fmt(entry.model, 30) : '—'} · thread: {fmt(entry.thread_id, 36)}
        </div>
      </div>

      {/* Query */}
      <Section title="Запрос">
        <Pre>{entry.query || '—'}</Pre>
        {(entry.query_source || entry.query_source_path?.length > 0) && (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted, #aaa)', fontFamily: 'monospace' }}>
            source: {entry.query_source || '—'}
            {entry.query_source_path?.length > 0 && (
              <> → {entry.query_source_path.join(' → ')}</>
            )}
          </div>
        )}
      </Section>

      {/* Reply */}
      {entry.reply && (
        <Section title="Ответ L1">
          <Pre>{entry.reply}</Pre>
        </Section>
      )}

      {/* Contract */}
      <Section title="Контракт OCM">
        <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.8 }}>
          <Row label="next_ocm_kind" value={entry.next_ocm_kind || '—'} />
          <Row label="carry_route" value={entry.carry_route_status || '—'} />
          <Row
            label="gap_total"
            value={entry.gap_total}
            color={entry.gap_total > 0 ? '#fb8c00' : '#43a047'}
          />
          {entry.gap_reasons?.length > 0 && (
            <Row label="gap_reasons" value={entry.gap_reasons.join(', ')} color="#fb8c00" />
          )}
        </div>
      </Section>

      {/* Flows */}
      {entry.flows?.length > 0 && (
        <Section title={`Потоки (${entry.flows.length})`}>
          <div>
            {entry.flows.map((f, i) => <FlowPill key={i} flow={f} />)}
          </div>
        </Section>
      )}

      {/* Actions */}
      {entry.actions?.length > 0 && (
        <Section title={`Действия (${entry.actions.length})`}>
          <div>
            {entry.actions.map((a, i) => <ActionPill key={i} action={a} />)}
          </div>
        </Section>
      )}

      {/* Context */}
      <ContextSection entry={entry} />
    </div>
  );
}

function ContextSection({ entry }) {
  const ctx = entry.ctx || {};
  const hasCtx = ctx.selected_n > 0 || ctx.scope || ctx.lane || entry.l2_hint || entry.memory_blocks_n > 0
    || entry.query_source_path?.length > 0 || entry.pw_summary?.length > 0;

  if (!hasCtx) return null;

  const scopeColor = ctx.fail_open ? '#fb8c00' : ctx.scope === 'thread_local' ? '#1976d2' : '#43a047';
  const scopeLabel = ctx.fail_open
    ? `global (fail_open: ${ctx.fail_open_reason || '?'})`
    : ctx.scope || '—';

  return (
    <Section title="Контекст">
      <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.9 }}>

        {/* Query source chain */}
        {entry.query_source_path?.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: 'var(--muted,#888)' }}>источник запроса: </span>
            {entry.query_source_path.map((s, i) => (
              <span key={i}>
                {i > 0 && <span style={{ color: 'var(--muted,#555)', margin: '0 4px' }}>→</span>}
                <span style={{ color: '#80cbc4', background: '#80cbc411', padding: '1px 5px', borderRadius: 4 }}>{s}</span>
              </span>
            ))}
          </div>
        )}

        {/* Memory retrieval */}
        {(ctx.selected_n > 0 || entry.memory_blocks_n > 0) && (
          <div style={{ marginBottom: 6 }}>
            <span style={{ color: 'var(--muted,#888)' }}>память: </span>
            <span style={{ color: '#e0e0e0' }}>
              {ctx.selected_n || entry.memory_blocks_n} блоков
            </span>
            {ctx.thread_hits > 0 && (
              <span style={{ color: '#1976d2', marginLeft: 8 }}>
                {ctx.thread_hits} из треда
              </span>
            )}
            {ctx.global_n > 0 && (
              <span style={{ color: '#9c27b0', marginLeft: 8 }}>
                {ctx.global_n} глобальных
              </span>
            )}
            {ctx.trust_avg > 0 && (
              <span style={{ color: 'var(--muted,#777)', marginLeft: 8 }}>
                trust avg={ctx.trust_avg}
                {ctx.trust_low_n > 0 && ` (${ctx.trust_low_n} low)`}
              </span>
            )}
          </div>
        )}

        {/* Scope / lane */}
        {ctx.scope && (
          <Row label="scope" value={scopeLabel} color={scopeColor} />
        )}
        {ctx.lane && ctx.lane !== 'all' && (
          <Row label="lane_filter" value={ctx.lane} />
        )}
        {ctx.strict_thinker && (
          <Row label="strict_thinker" value="да" color="#fb8c00" />
        )}
        {ctx.thread_scope_locked && ctx.thread_scope_lock_reason && (
          <Row label="thread_lock" value={ctx.thread_scope_lock_reason} color="#fb8c00" />
        )}
        {ctx.domain_seed_source && (
          <Row label="domain_seed" value={ctx.domain_seed_source} />
        )}
        {ctx.vector_search_skipped && (
          <Row label="vector_skip" value={ctx.vector_search_skip_reason || 'да'} color="#fb8c00" />
        )}

        {/* Retrieved vs selected */}
        {ctx.retrieved_n > 0 && (
          <Row label="retrieved→selected" value={`${ctx.retrieved_n} → ${ctx.selected_n}`} />
        )}

        {/* Participant window */}
        {entry.pw_summary?.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <span style={{ color: 'var(--muted,#888)' }}>участники: </span>
            {entry.pw_summary.map((r, i) => (
              <span key={i} style={{ marginRight: 6, padding: '1px 6px', borderRadius: 8, background: '#ffffff11', fontSize: 11 }}>{r}</span>
            ))}
          </div>
        )}

        {/* L2 hint */}
        {entry.l2_hint && (
          <div style={{ marginTop: 6 }}>
            <div style={{ color: 'var(--muted,#888)', marginBottom: 2 }}>l2_hint:</div>
            <div style={{ color: '#ce93d8', paddingLeft: 8, borderLeft: '2px solid #9c27b044', lineHeight: 1.5 }}>
              {entry.l2_hint}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: 'var(--muted, #888)',
        marginBottom: 6,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div>
      <span style={{ color: 'var(--muted, #888)', marginRight: 6 }}>{label}:</span>
      <span style={color ? { color } : {}}>{String(value ?? '—')}</span>
    </div>
  );
}

function Pre({ children }) {
  return (
    <div style={{
      background: 'var(--pre-bg, #1a1a1a)',
      border: '1px solid var(--border, #333)',
      borderRadius: 6,
      padding: '8px 12px',
      fontSize: 13,
      lineHeight: 1.5,
      fontFamily: 'inherit',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxHeight: 200,
      overflowY: 'auto',
    }}>
      {children}
    </div>
  );
}

function ListEntry({ entry, selected, onClick }) {
  const dot = gapColor(entry);
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid var(--border, #2a2a2a)',
        background: selected ? 'var(--selected-bg, #1976d222)' : 'transparent',
        borderLeft: selected ? '3px solid #1976d2' : '3px solid transparent',
        transition: 'background 0.1s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0,
        }} />
        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>
          #{entry.ocm_n}
        </span>
        <span style={{
          fontSize: 11,
          padding: '0 6px',
          borderRadius: 8,
          background: 'var(--tag-bg, #ffffff11)',
          color: 'var(--muted, #aaa)',
          flexShrink: 0,
        }}>
          {sourceLabel(entry.source)}
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted, #777)', marginLeft: 'auto', flexShrink: 0 }}>
          {fmtTs(entry.ts)}
        </span>
      </div>
      {entry.query && (
        <div style={{
          fontSize: 12,
          color: 'var(--muted, #aaa)',
          paddingLeft: 16,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {fmt(entry.query, 60)}
        </div>
      )}
      {(entry.gap_total > 0 || entry.error) && (
        <div style={{ fontSize: 11, paddingLeft: 16, color: dot, marginTop: 1 }}>
          {entry.error || `gap=${entry.gap_total}`}
          {entry.elapsed_s != null ? ` · ${entry.elapsed_s}s` : ''}
        </div>
      )}
      {!entry.gap_total && !entry.error && entry.elapsed_s != null && (
        <div style={{ fontSize: 11, paddingLeft: 16, color: 'var(--muted, #666)', marginTop: 1 }}>
          {entry.elapsed_s}s
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 50;

// ─── Thread summary row ───────────────────────────────────────────────────────

function ThreadRow({ thread, onDrill }) {
  const hasError = !!thread.last_error;
  const hasGaps = (thread.total_gaps || 0) > 0;
  const dotColor = hasError ? '#e53935' : hasGaps ? '#fb8c00' : '#43a047';

  const fmtTid = (tid) => {
    if (!tid) return '—';
    if (tid.length <= 48) return tid;
    return tid.slice(0, 22) + '…' + tid.slice(-22);
  };

  return (
    <div
      onClick={() => onDrill(thread.thread_id)}
      title={thread.thread_id}
      style={{
        padding: '10px 16px',
        borderBottom: '1px solid var(--border, #2a2a2a)',
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--selected-bg, #1976d211)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{ fontFamily: 'monospace', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fmtTid(thread.thread_id)}
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted,#777)', flexShrink: 0 }}>
          {fmtTs(thread.last_ts)}
        </span>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 10, paddingLeft: 16, flexWrap: 'wrap' }}>
        <Stat label="тиков" value={thread.total_ticks ?? '?'} />
        <Stat label="gaps" value={thread.total_gaps ?? '?'} color={hasGaps ? '#fb8c00' : undefined} />
        <Stat label="OCM#" value={thread.last_ocm_n ?? '?'} />
        {thread.last_source && (
          <Stat label="source" value={sourceLabel(thread.last_source)} />
        )}
        {thread.last_model && (
          <Stat label="model" value={fmt(thread.last_model, 20)} />
        )}
      </div>

      {/* Error */}
      {hasError && (
        <div style={{ paddingLeft: 16, marginTop: 3, fontSize: 11, color: '#e53935', fontFamily: 'monospace' }}>
          err: {fmt(thread.last_error, 80)}
        </div>
      )}

      {/* Query preview */}
      {thread.last_query && (
        <div style={{ paddingLeft: 16, marginTop: 3, fontSize: 12, color: 'var(--muted,#888)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fmt(thread.last_query, 80)}
        </div>
      )}

      {/* Flow pills */}
      {thread.flow_state?.length > 0 && (
        <div style={{ paddingLeft: 16, marginTop: 4 }}>
          {thread.flow_state.slice(0, 5).map((f, i) => <FlowPill key={i} flow={f} />)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <span style={{ fontSize: 11, color: 'var(--muted,#777)' }}>
      {label}:{' '}
      <span style={{ color: color || 'var(--ink,#ccc)', fontFamily: 'monospace' }}>{String(value ?? '—')}</span>
    </span>
  );
}

// ─── Threads panel ────────────────────────────────────────────────────────────

function ThreadsPanel({ apiBase, onDrill }) {
  const [threads, setThreads] = useState([]);
  const [dbSource, setDbSource] = useState('');
  const [error, setError] = useState('');

  const fetchThreads = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/ocm/threads?limit=100`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.ok) {
        setThreads(data.threads || []);
        setDbSource(data.source || '');
        setError('');
      }
    } catch (e) {
      setError(String(e));
    }
  }, [apiBase]);

  useEffect(() => {
    fetchThreads();
    const id = setInterval(fetchThreads, POLL_MS);
    return () => clearInterval(id);
  }, [fetchThreads]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        padding: '8px 16px',
        borderBottom: '1px solid var(--border,#333)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 12, color: 'var(--muted,#888)' }}>
          {threads.length} потоков
          {dbSource === 'db' ? ' · DB' : dbSource === 'ring' ? ' · memory' : ''}
        </span>
        {error && <span style={{ fontSize: 12, color: '#e53935' }}>{error}</span>}
        <button onClick={fetchThreads}
          style={{ marginLeft: 'auto', background: 'none', border: '1px solid var(--border,#444)', borderRadius: 6, cursor: 'pointer', color: 'var(--ink,#e0e0e0)', fontSize: 12, padding: '3px 10px' }}>↻</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {threads.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--muted,#888)', fontSize: 13 }}>
            Нет активных потоков. Запустите тик.
          </div>
        ) : (
          threads.map((t) => (
            <ThreadRow
              key={t.thread_id}
              thread={t}
              onDrill={onDrill}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function OcmTimeline({ apiBase, onExit }) {
  const [tab, setTab] = useState('timeline');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selectedSeq, setSelectedSeq] = useState(null);
  const [error, setError] = useState('');
  const [dbSource, setDbSource] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [filterGap, setFilterGap] = useState(false);
  const [filterErrors, setFilterErrors] = useState(false);
  const [filterThread, setFilterThread] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const listRef = useRef(null);
  const prevTotalRef = useRef(0);

  const fetchHistory = useCallback(async (offsetOverride) => {
    const off = offsetOverride !== undefined ? offsetOverride : offset;
    const params = new URLSearchParams({
      limit: PAGE_SIZE,
      offset: off,
      ...(filterGap ? { min_gap: 1 } : {}),
      ...(filterErrors ? { errors_only: 1 } : {}),
      ...(filterThread ? { thread_id: filterThread } : {}),
      ...(filterSource ? { source: filterSource } : {}),
    });
    try {
      const res = await fetch(`${apiBase}/ocm/history?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.ok) {
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        setDbSource(data.source || '');
        setError('');
      }
    } catch (e) {
      setError(String(e));
    }
  }, [apiBase, offset, filterGap, filterErrors, filterThread, filterSource]);

  useEffect(() => {
    fetchHistory();
    const id = setInterval(fetchHistory, POLL_MS);
    return () => clearInterval(id);
  }, [fetchHistory]);

  useEffect(() => {
    if (autoScroll && offset === 0 && entries.length > prevTotalRef.current && listRef.current) {
      listRef.current.scrollTop = 0;
    }
    prevTotalRef.current = entries.length;
  }, [entries, autoScroll, offset]);

  const selectedEntry = entries.find((e) => e.seq === selectedSeq) || null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const goToPage = (page) => {
    const newOffset = (page - 1) * PAGE_SIZE;
    setOffset(newOffset);
    fetchHistory(newOffset);
  };

  // Called from ThreadsPanel: switch to timeline filtered by that thread
  const drillIntoThread = (threadId) => {
    setFilterThread(threadId);
    setFilterGap(false);
    setFilterErrors(false);
    setFilterSource('');
    setOffset(0);
    setTab('timeline');
  };

  const tabBtn = (name, label) => (
    <button
      onClick={() => setTab(name)}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: tab === name ? '2px solid #1976d2' : '2px solid transparent',
        color: tab === name ? '#1976d2' : 'var(--muted,#aaa)',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: tab === name ? 700 : 400,
        padding: '6px 14px',
        marginBottom: -1,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: 'var(--bg, #121212)',
      color: 'var(--ink, #e0e0e0)',
      fontFamily: 'system-ui, sans-serif',
    }}>
      {/* Header */}
      <div style={{
        flexShrink: 0,
        background: 'var(--surface, #1e1e1e)',
        borderBottom: '1px solid var(--border, #333)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px 0', flexWrap: 'wrap' }}>
          <button
            onClick={onExit}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink, #e0e0e0)', fontSize: 18, padding: '2px 6px', marginBottom: 6 }}
            title="Назад"
          >←</button>
          <span style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>OCM</span>
          {tabBtn('timeline', 'Timeline')}
          {tabBtn('threads', 'Потоки')}

          {tab === 'timeline' && (
            <>
              <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10, background: 'var(--tag-bg, #ffffff11)', color: 'var(--muted, #aaa)', marginLeft: 4, marginBottom: 6 }}>
                {total} {dbSource === 'db' ? '· DB' : dbSource === 'ring' ? '· mem' : ''}
              </span>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                  <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}
                    style={{ background: 'none', border: '1px solid var(--border,#444)', borderRadius: 4, color: 'var(--ink,#e0e0e0)', cursor: 'pointer', padding: '2px 7px', fontSize: 12 }}>‹</button>
                  <span style={{ fontSize: 12, color: 'var(--muted,#aaa)' }}>{currentPage}/{totalPages}</span>
                  <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}
                    style={{ background: 'none', border: '1px solid var(--border,#444)', borderRadius: 4, color: 'var(--ink,#e0e0e0)', cursor: 'pointer', padding: '2px 7px', fontSize: 12 }}>›</button>
                </div>
              )}
              <label style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted, #aaa)', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginBottom: 6 }}>
                <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
                Авто
              </label>
              <button onClick={() => fetchHistory()}
                style={{ background: 'none', border: '1px solid var(--border,#444)', borderRadius: 6, cursor: 'pointer', color: 'var(--ink,#e0e0e0)', fontSize: 12, padding: '3px 10px', marginBottom: 6 }}>↻</button>
              {error && <span style={{ fontSize: 12, color: '#e53935', marginBottom: 6 }}>{error}</span>}
            </>
          )}
        </div>

        {/* Filter bar — only for timeline tab */}
        {tab === 'timeline' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px 10px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--muted,#aaa)', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={filterGap} onChange={(e) => { setFilterGap(e.target.checked); setOffset(0); }} />
              Gap&gt;0
            </label>
            <label style={{ fontSize: 12, color: 'var(--muted,#aaa)', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={filterErrors} onChange={(e) => { setFilterErrors(e.target.checked); setOffset(0); }} />
              Ошибки
            </label>
            <input
              placeholder="source…"
              value={filterSource}
              onChange={(e) => { setFilterSource(e.target.value); setOffset(0); }}
              style={{ fontSize: 12, padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border,#444)', background: 'var(--input-bg,#1a1a1a)', color: 'var(--ink,#e0e0e0)', width: 120 }}
            />
            <input
              placeholder="thread_id…"
              value={filterThread}
              onChange={(e) => { setFilterThread(e.target.value); setOffset(0); }}
              style={{ fontSize: 12, padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border,#444)', background: 'var(--input-bg,#1a1a1a)', color: 'var(--ink,#e0e0e0)', width: 180 }}
            />
            {(filterGap || filterErrors || filterSource || filterThread) && (
              <button
                onClick={() => { setFilterGap(false); setFilterErrors(false); setFilterSource(''); setFilterThread(''); setOffset(0); }}
                style={{ fontSize: 12, background: 'none', border: '1px solid #e53935', borderRadius: 4, color: '#e53935', cursor: 'pointer', padding: '2px 8px' }}
              >✕ сброс</button>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      {tab === 'threads' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ThreadsPanel apiBase={apiBase} onDrill={drillIntoThread} />
        </div>
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div ref={listRef} style={{ width: 280, minWidth: 220, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid var(--border, #333)', background: 'var(--surface, #1a1a1a)' }}>
            {entries.length === 0 ? (
              <div style={{ padding: 20, fontSize: 13, color: 'var(--muted, #888)' }}>Ожидание тиков…</div>
            ) : (
              entries.map((entry) => (
                <ListEntry
                  key={entry.seq}
                  entry={entry}
                  selected={entry.seq === selectedSeq}
                  onClick={() => setSelectedSeq(entry.seq === selectedSeq ? null : entry.seq)}
                />
              ))
            )}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg, #121212)' }}>
            <DetailPanel entry={selectedEntry} />
          </div>
        </div>
      )}
    </div>
  );
}
